import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { Target, Plus, Pencil, Trash2, CalendarClock } from "lucide-react";

const STATUS_TONE = {
  on_track: "text-[#00E65B] border-[#00E65B]/40",
  at_risk: "text-[#FFB800] border-[#FFB800]/40",
  off_track: "text-red-400 border-red-400/40",
  unknown: "text-muted-foreground border-border",
};

function SptDialog({ caseId, spt, targetOptions, frequencies, onSaved, onClose }) {
  const [f, setF] = useState(spt || { direction: "decrease", verification_frequency: "annual" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickTarget = (tid) => {
    const t = targetOptions.find((x) => x.id === tid);
    setF((p) => ({
      ...p, target_id: tid === "none" ? null : tid,
      ...(t ? { name: p.name || t.name, metric_label: t.metric, unit: t.unit,
                baseline_year: t.baseline_year, baseline_value: t.baseline_value,
                target_value: t.target_value, deadline_year: t.target_year } : {}),
    }));
  };
  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: f.name || null, target_id: f.target_id || null, metric_label: f.metric_label || null,
        unit: f.unit || null, baseline_year: numOrNull(f.baseline_year), baseline_value: numOrNull(f.baseline_value),
        target_value: numOrNull(f.target_value), deadline_year: numOrNull(f.deadline_year),
        direction: f.direction || null, margin_bps: numOrNull(f.margin_bps),
        verification_frequency: f.verification_frequency || null,
        next_verification_date: f.next_verification_date || null, verifier: f.verifier || null,
        current_value: numOrNull(f.current_value),
      };
      const r = spt?.id
        ? await api.put(`/green-finance/cases/${caseId}/spts/${spt.id}`, body)
        : await api.post(`/green-finance/cases/${caseId}/spts`, body);
      onSaved(r.data);
      toast.success(spt?.id ? "SPT updated" : "SPT added");
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="sll-spt-dialog">
        <DialogHeader>
          <DialogTitle>{spt?.id ? "Edit" : "Add"} Sustainability Performance Target</DialogTitle>
          <DialogDescription>Link a live ESG target or define the SPT manually.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[10px]">Link to ESG target (auto-fills values)</Label>
            <Select value={f.target_id || "none"} onValueChange={pickTarget}>
              <SelectTrigger data-testid="sll-spt-target"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not linked (manual)</SelectItem>
                {targetOptions.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label className="text-[10px]">SPT name</Label>
            <Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Scope 1+2 intensity reduction" data-testid="sll-spt-name" /></div>
          <div><Label className="text-[10px]">Metric</Label>
            <Input value={f.metric_label || ""} onChange={(e) => set("metric_label", e.target.value)} placeholder="Scope 1+2 GHG" /></div>
          <div><Label className="text-[10px]">Unit</Label>
            <Input value={f.unit || ""} onChange={(e) => set("unit", e.target.value)} placeholder="tCO2e" /></div>
          <div><Label className="text-[10px]">Baseline year</Label>
            <Input type="number" value={f.baseline_year ?? ""} onChange={(e) => set("baseline_year", e.target.value)} data-testid="sll-spt-baseline-year" /></div>
          <div><Label className="text-[10px]">Baseline value</Label>
            <Input type="number" value={f.baseline_value ?? ""} onChange={(e) => set("baseline_value", e.target.value)} data-testid="sll-spt-baseline-value" /></div>
          <div><Label className="text-[10px]">Target value</Label>
            <Input type="number" value={f.target_value ?? ""} onChange={(e) => set("target_value", e.target.value)} data-testid="sll-spt-target-value" /></div>
          <div><Label className="text-[10px]">Deadline year</Label>
            <Input type="number" value={f.deadline_year ?? ""} onChange={(e) => set("deadline_year", e.target.value)} data-testid="sll-spt-deadline" /></div>
          <div><Label className="text-[10px]">Direction</Label>
            <Select value={f.direction || "decrease"} onValueChange={(v) => set("direction", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="decrease">Decrease</SelectItem><SelectItem value="increase">Increase</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-[10px]">Margin ratchet (bps)</Label>
            <Input type="number" value={f.margin_bps ?? ""} onChange={(e) => set("margin_bps", e.target.value)} placeholder="5" data-testid="sll-spt-margin" /></div>
          <div><Label className="text-[10px]">Verification frequency</Label>
            <Select value={f.verification_frequency || "annual"} onValueChange={(v) => set("verification_frequency", v)}>
              <SelectTrigger data-testid="sll-spt-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>{frequencies.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[10px]">Next verification date</Label>
            <Input type="date" value={f.next_verification_date || ""} onChange={(e) => set("next_verification_date", e.target.value)} data-testid="sll-spt-verification-date" /></div>
          <div><Label className="text-[10px]">Verifier</Label>
            <Input value={f.verifier || ""} onChange={(e) => set("verifier", e.target.value)} placeholder="External assurer / bank agent" /></div>
          <div><Label className="text-[10px]">Current value override (optional)</Label>
            <Input type="number" value={f.current_value ?? ""} onChange={(e) => set("current_value", e.target.value)} placeholder="uses live target data if empty" /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy} data-testid="sll-spt-save">{busy ? "Saving…" : "Save SPT"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SllTargetsPanel({ caseId }) {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    api.get(`/green-finance/cases/${caseId}/sll`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [caseId]);
  if (!d) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading SLL targets…</div>;

  const del = async (s) => {
    try {
      const r = await api.delete(`/green-finance/cases/${caseId}/spts/${s.id}`);
      setD(r.data); toast.success("SPT removed");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const sm = d.summary;

  return (
    <div className="space-y-3" data-testid="sll-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 min-w-[300px]">
          {[["SPTs", sm.total], ["On track", sm.on_track, "text-[#00E65B]"], ["At risk / off track", sm.at_risk + sm.off_track, sm.at_risk + sm.off_track ? "text-[#FFB800]" : ""],
            ["Net margin effect", `${sm.net_margin_effect_bps > 0 ? "+" : ""}${sm.net_margin_effect_bps} bps`, sm.net_margin_effect_bps < 0 ? "text-[#00E65B]" : sm.net_margin_effect_bps > 0 ? "text-red-400" : ""]].map(([l, v, tone]) => (
            <div key={l} className="card-flat p-2.5" data-testid={`sll-summary-${String(l).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{l}</div>
              <div className={`font-mono text-lg font-bold ${tone || ""}`}>{v}</div>
            </div>
          ))}
        </div>
        <Button size="sm" variant="primary" onClick={() => setEditing({})} data-testid="sll-add-spt">
          <Plus className="h-3.5 w-3.5" /> Add SPT
        </Button>
      </div>
      {sm.next_verification && (
        <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
          <CalendarClock className="h-3.5 w-3.5" /> Next verification: {sm.next_verification} — shown on the Funding Calendar
        </div>
      )}

      <div className="space-y-2">
        {d.spts.map((s) => (
          <div key={s.id} className="card-flat p-3 space-y-1.5" data-testid={`sll-spt-row-${s.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Target className="h-3.5 w-3.5 text-accent" />
                <span className="font-display font-semibold text-sm">{s.name}</span>
                <Badge variant="outline" className={`text-[8px] uppercase ${STATUS_TONE[s.status]}`} data-testid={`sll-spt-status-${s.id}`}>{s.status_label}</Badge>
                {s.verification_overdue && <Badge variant="outline" className="text-[8px] uppercase text-red-400 border-red-400/40">Verification overdue</Badge>}
                {s.linked_target_name && <Badge variant="outline" className="text-[8px]">{s.linked_target_name}</Badge>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(s)} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`sll-spt-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(s)} className="p-1 text-muted-foreground hover:text-red-400" data-testid={`sll-spt-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]">
              <div><span className="text-muted-foreground">Baseline:</span> <span className="font-mono">{formatNum(s.baseline_value)} {s.unit}</span> ({s.baseline_year})</div>
              <div><span className="text-muted-foreground">Target:</span> <span className="font-mono">{formatNum(s.target_value)}</span> by {s.deadline_year}</div>
              <div><span className="text-muted-foreground">Current:</span> <span className="font-mono">{s.current_value != null ? formatNum(s.current_value) : "—"}</span>{s.current_source === "target" ? " (live)" : s.current_source === "manual" ? " (manual)" : ""}</div>
              <div><span className="text-muted-foreground">Required now:</span> <span className="font-mono">{s.expected_now != null ? formatNum(s.expected_now) : "—"}</span></div>
              <div><span className="text-muted-foreground">Margin:</span> <span className={`font-mono ${s.margin_effect_bps < 0 ? "text-[#00E65B]" : s.margin_effect_bps > 0 ? "text-red-400" : ""}`}>{s.margin_bps != null ? `${s.margin_effect_bps > 0 ? "+" : ""}${s.margin_effect_bps} bps` : "—"}</span></div>
            </div>
            <div className="text-[10px] text-muted-foreground">
              {s.verification_frequency ? `${s.verification_frequency} verification` : "verification schedule not set"}
              {s.next_verification_date ? ` · next ${s.next_verification_date}` : ""}{s.verifier ? ` · ${s.verifier}` : ""}
            </div>
          </div>
        ))}
        {d.spts.length === 0 && (
          <div className="card-flat p-8 text-center text-muted-foreground text-sm" data-testid="sll-empty">
            <Target className="h-6 w-6 mx-auto mb-2 opacity-50" />
            No Sustainability Performance Targets yet — add SPTs to track SLL margin ratchets against your live ESG data.
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>

      {editing !== null && (
        <SptDialog caseId={caseId} spt={editing.id ? editing : null} targetOptions={d.target_options}
          frequencies={d.frequencies} onSaved={setD} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
